import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { SICHTBARKEITEN, WILDARTEN } from "@dj/domain/drueckjagd";
import { Dialog } from "../design/Dialog";
import { Ankreuzfeld, Auswahl, Schalter, Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useDokument, useSammlung } from "../plattform/lesemodelle";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { BeteiligungDialog } from "./Beteiligung";
import { JagdzeitplanDialog, jagddatumAus } from "./Jagdzeitplan";
import { Abschnitt, Angaben, wert, useKopf, useVeranstalter } from "./teile";

/**
 * Planung (oberflaeche.md §6): Jagdherr und Jagdleiter. Inhalte der Drückjagd werden als vollständige neue Liste
 * gepflegt (befehle.md §6.3); Bekanntgeben, Abschließen und Absagen verlangen eine Bestätigung (`alertdialog`).
 * Jagdzeitplan und Beteiligung sind Dialoge (V-24); ändern darf beides nur der Jagdherr des Veranstalters, alle anderen
 * Leser sehen den Stand.
 */
type Liste = "Tagesablauf" | "Sicherheitsbestimmungen" | "Hinweise" | "Notfallkontakte" | "Rettungspunkte";

export function Planung() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const kopf = useKopf(id);
  const { veranstalter, jagdherrDesVeranstalters } = useVeranstalter(id);
  const einstellungen = useDokument(`drueckjagden/${id}/planung/jagdeinstellungen`);
  const [grund, setGrund] = useState<string | null>(null);
  const [status, setStatus] = useState<null | "Bekanntgeben" | "Abschließen" | "Absagen">(null);
  const [liste, setListe] = useState<Liste | null>(null);
  const [dialog, setDialog] = useState<null | "Jagdeinstellungen" | "Kopieren" | "Jagdzeitplan" | "Beteiligung">(null);
  const ziel = (pfad: string) => () => navigate(`/drueckjagd/${id}/${pfad}`);

  async function statusSenden() {
    const pfad = { Bekanntgeben: "DrueckjagdBekanntgeben", "Abschließen": "DrueckjagdAbschliessen", Absagen: "DrueckjagdAbsagen" }[status!];
    setGrund(null);
    const a = await befehl(pfad, { "Drückjagd": id });
    setStatus(null);
    if (!a.ok) setGrund(abweisungsgrund(a));
  }

  const k = kopf.daten;
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Planung">
      <Abschnitt name="Drückjagd" symbol="drueckjagden">
        <Angaben zeilen={[["Drückjagd", wert(k?.["Drückjagd"])], ["Jagddatum", wert(k?.["Jagddatum"])], ["Treffpunkt", wert(k?.["Treffpunkt"])],
          ["Drückjagdstatus", wert(k?.["Drückjagdstatus"])]]} />
      </Abschnitt>
      <Abweisung grund={grund} />
      <div className="knopfreihe">
        <Knopf art="planung" symbol="einstellungen" onPress={() => setDialog("Jagdeinstellungen")}>Jagdeinstellungen</Knopf>
        <Knopf art="planung" symbol="teilnehmer" onPress={ziel("einteilung")}>Einteilung</Knopf>
        <Knopf art="planung" symbol="freigabe" onPress={ziel("freigabe")}>Freigabe</Knopf>
        <Knopf art="planung" symbol="uhr" onPress={() => setDialog("Jagdzeitplan")}>Jagdzeitplan</Knopf>
        <Knopf art="planung-zweit" symbol="tag" onPress={() => setListe("Tagesablauf")}>Tagesablauf</Knopf>
        <Knopf art="planung-zweit" symbol="info" onPress={() => setListe("Sicherheitsbestimmungen")}>Sicherheitsbestimmungen</Knopf>
        <Knopf art="planung-zweit" symbol="info" onPress={() => setListe("Hinweise")}>Hinweise</Knopf>
        <Knopf art="planung-zweit" symbol="telefon" onPress={() => setListe("Notfallkontakte")}>Notfallkontakte</Knopf>
        <Knopf art="planung-zweit" symbol="rettungspunkt" onPress={() => setListe("Rettungspunkte")}>Rettungspunkte</Knopf>
        <Knopf art="planung-zweit" symbol="link" onPress={() => setDialog("Beteiligung")}>Beteiligung</Knopf>
        <Knopf art="planung-zweit" symbol="teilnehmer" onPress={ziel("teilnehmer")}>Teilnehmerübersicht</Knopf>
        <Knopf art="planung-zweit" symbol="jagdschein" onPress={ziel("pruefprotokoll")}>Prüfprotokoll</Knopf>
        <Knopf art="planung-zweit" symbol="drueckjagden" onPress={() => setDialog("Kopieren")}>Kopieren</Knopf>
        <Knopf art="planung" symbol="senden" onPress={() => setStatus("Bekanntgeben")}>Bekanntgeben</Knopf>
        <Knopf art="planung-zweit" symbol="haken" onPress={() => setStatus("Abschließen")}>Abschließen</Knopf>
        <Knopf art="planung-zweit" symbol="schliessen" onPress={() => setStatus("Absagen")}>Absagen</Knopf>
      </div>
      <Dialog name={status ?? "Bekanntgeben"} offen={!!status} onSchliessen={() => setStatus(null)} bestaetigung>
        <p>{status === "Bekanntgeben" ? "Mit der Bekanntgabe sehen alle Mitwirkenden Einweisung, Einteilung und Freigabe."
          : status === "Abschließen" ? "Nach dem Abschluss bleiben Tagesablauf und Bestimmungen unverändert." : "Die Drückjagd wird abgesagt."}</p>
        <div className="knopfreihe">
          <Knopf onPress={statusSenden}>Bestätigen</Knopf>
          <Knopf art="zweit" onPress={() => setStatus(null)}>Abbrechen</Knopf>
        </div>
      </Dialog>
      <JagdeinstellungenDialog id={id} offen={dialog === "Jagdeinstellungen"} daten={einstellungen.daten} onSchliessen={() => setDialog(null)} />
      <KopierenDialog id={id} offen={dialog === "Kopieren"} onSchliessen={() => setDialog(null)} />
      <JagdzeitplanDialog id={id} offen={dialog === "Jagdzeitplan"} jagddatum={jagddatumAus(k?.["Jagddatum"])} bearbeitbar={jagdherrDesVeranstalters}
        onSchliessen={() => setDialog(null)} />
      <BeteiligungDialog id={id} offen={dialog === "Beteiligung"} veranstalter={veranstalter} veranstalterName={k?.["Veranstalter"] ? String(k["Veranstalter"]) : null}
        bearbeitbar={jagdherrDesVeranstalters} onSchliessen={() => setDialog(null)} />
      <ListenDialog id={id} liste={liste} onSchliessen={() => setListe(null)} />
    </AnsichtDerDrueckjagd>
  );
}

function JagdeinstellungenDialog({ id, offen, daten, onSchliessen }: { id: string; offen: boolean; daten: Record<string, unknown> | null; onSchliessen: () => void }) {
  const [wildarten, setWildarten] = useState<string[] | null>(null);
  const [sicht, setSicht] = useState<string | null>(null);
  const [nachweis, setNachweis] = useState<boolean | null>(null);
  const [alter, setAlter] = useState<string | null>(null);
  const [grund, setGrund] = useState<string | null>(null);
  const w = wildarten ?? ((daten?.["Wildarten dieser Drückjagd"] as string[] | undefined) ?? [...WILDARTEN]);
  const s = sicht ?? ((daten?.["Sichtbarkeit der Standbesetzung für Schützen"] as string | undefined) ?? null);
  const n = nachweis ?? (daten?.["Schießnachweis erforderlich"] === true);
  const a = alter ?? (daten?.["höchstes Alter in Monaten"] != null ? String(daten["höchstes Alter in Monaten"]) : "");
  async function speichern() {
    setGrund(null);
    const d: Record<string, unknown> = { "Drückjagd": id, "Wildarten dieser Drückjagd": w, "Schießnachweis erforderlich": n };
    if (s) d["Sichtbarkeit der Standbesetzung für Schützen"] = s;
    if (n && a) d["höchstes Alter in Monaten"] = Number(a);
    const r = await befehl("JagdeinstellungenAendern", d);
    if (!r.ok) { setGrund(abweisungsgrund(r)); return; }
    onSchliessen();
  }
  return (
    <Dialog name="Jagdeinstellungen" symbol="einstellungen" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        <div className="filter" role="group" aria-label="Wildarten dieser Drückjagd">
          {WILDARTEN.map((x) => <Ankreuzfeld key={x} label={x} an={w.includes(x)} onChange={(an) => setWildarten(an ? [...w, x] : w.filter((y) => y !== x))} />)}
        </div>
        <Auswahl label="Sichtbarkeit der Standbesetzung für Schützen" optionen={SICHTBARKEITEN} wert={s} onChange={setSicht} />
        <Schalter label="Schießnachweis erforderlich" an={n} onChange={setNachweis} />
        {n ? <Textfeld label="höchstes Alter in Monaten" wert={a} onChange={setAlter} eingabemodus="numeric" /> : null}
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

function KopierenDialog({ id, offen, onSchliessen }: { id: string; offen: boolean; onSchliessen: () => void }) {
  const navigate = useNavigate();
  const [felder, setFelder] = useState({ Bezeichnung: "", Jagddatum: "", Treffpunkt: "" });
  const [grund, setGrund] = useState<string | null>(null);
  async function speichern() {
    setGrund(null);
    const a = await befehl("DrueckjagdKopieren", { "Drückjagd": id, ...felder });
    if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    onSchliessen();
    if (a.kennungen["Drueckjagd"]) navigate(`/drueckjagd/${a.kennungen["Drueckjagd"]}/planung`);
  }
  return (
    <Dialog name="Kopieren" symbol="drueckjagden" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        {(["Bezeichnung", "Jagddatum", "Treffpunkt"] as const).map((f) => (
          <Textfeld key={f} label={f} wert={felder[f]} onChange={(v) => setFelder({ ...felder, [f]: v })} hilfe={f === "Jagddatum" ? "JJJJ-MM-TT" : undefined} />
        ))}
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

const LISTEN: Record<Liste, { sammlung: string; befehl: string; felder: string[] }> = {
  Tagesablauf: { sammlung: "tagesablauf", befehl: "TagesablaufPflegen", felder: ["Uhrzeit", "Programmpunkt"] },
  Sicherheitsbestimmungen: { sammlung: "sicherheitsbestimmungen", befehl: "SicherheitsbestimmungenPflegen", felder: ["Sicherheitsbestimmung"] },
  Hinweise: { sammlung: "hinweise", befehl: "HinweisePflegen", felder: ["Hinweis"] },
  Notfallkontakte: { sammlung: "notfallkontakte", befehl: "NotfallkontaktePflegen", felder: ["Art", "Bezeichnung", "Telefonnummer", "Adresse"] },
  Rettungspunkte: { sammlung: "rettungspunkte", befehl: "RettungspunktePflegen", felder: ["Kennung", "Breite", "Länge", "Lagebeschreibung"] },
};

/** Eintrag des Lesemodells → Element der vollständigen neuen Liste des Pflege-Befehls. */
function alsEintrag(liste: Liste, d: Record<string, unknown>): unknown {
  switch (liste) {
    case "Tagesablauf": return { Uhrzeit: d["Uhrzeit"], "ca.": d["ca."] === true, Programmpunkt: d["Programmpunkt"] };
    case "Sicherheitsbestimmungen": return d["Sicherheitsbestimmung"];
    case "Hinweise": return d["Hinweis"];
    case "Notfallkontakte": return { Art: d["Art"], Bezeichnung: d["Bezeichnung"], Telefonnummer: d["Anruf"] ?? d["Telefonnummer"], ...(d["Adresse"] ? { Adresse: d["Adresse"] } : {}) };
    case "Rettungspunkte": return { Kennung: d["Kennung"], Breite: Number(d["Breite"]), Länge: Number(d["Länge"]), ...(d["Lage"] ?? d["Lagebeschreibung"] ? { Lagebeschreibung: d["Lage"] ?? d["Lagebeschreibung"] } : {}) };
  }
}

function anzeige(liste: Liste, d: Record<string, unknown>): string {
  switch (liste) {
    case "Tagesablauf": return `${String(d["Uhrzeit"])}${d["ca."] ? " ca." : ""} ${String(d["Programmpunkt"])}`;
    case "Sicherheitsbestimmungen": return String(d["Sicherheitsbestimmung"]);
    case "Hinweise": return String(d["Hinweis"]);
    case "Notfallkontakte": return `${String(d["Bezeichnung"])} · ${String(d["Anruf"] ?? "")}`;
    case "Rettungspunkte": return `${String(d["Kennung"])} · ${String(d["Lage"] ?? "")}`;
  }
}

function ListenDialog({ id, liste, onSchliessen }: { id: string; liste: Liste | null; onSchliessen: () => void }) {
  const art = liste ? LISTEN[liste] : null;
  const eintraege = useSammlung(art ? `drueckjagden/${id}/${art.sammlung}` : null);
  const [neu, setNeu] = useState<Record<string, string>>({});
  const [ca, setCa] = useState(false);
  const [grund, setGrund] = useState<string | null>(null);
  async function senden(alle: unknown[]) {
    if (!liste || !art) return;
    setGrund(null);
    const a = await befehl(art.befehl, { "Drückjagd": id, [liste]: alle });
    if (!a.ok) setGrund(abweisungsgrund(a));
    else { setNeu({}); setCa(false); }
  }
  const bisher = liste ? eintraege.daten.map((d) => alsEintrag(liste, d)) : [];
  function hinzufuegen() {
    if (!liste) return;
    const d: Record<string, unknown> = { ...neu };
    if (liste === "Tagesablauf") d["ca."] = ca;
    if (liste === "Rettungspunkte") { d["Breite"] = Number(neu["Breite"]); d["Länge"] = Number(neu["Länge"]); }
    const e = liste === "Sicherheitsbestimmungen" ? neu["Sicherheitsbestimmung"] : liste === "Hinweise" ? neu["Hinweis"] : d;
    void senden([...bisher, e]);
  }
  return (
    <Dialog name={liste ?? "Tagesablauf"} offen={!!liste} onSchliessen={onSchliessen}>
      <div className="formular">
        <ul className="liste-zeilen">
          {liste ? eintraege.daten.map((d, i) => (
            <li key={d._id}>
              <span className="liste-zeilen__text">{anzeige(liste, d)}</span>
              <Knopf art="planung-zweit" symbol="schliessen" onPress={() => void senden(bisher.filter((_, j) => j !== i))}>Entfernen</Knopf>
            </li>
          )) : null}
        </ul>
        {art?.felder.map((f) => <Textfeld key={f} label={f} wert={neu[f] ?? ""} onChange={(v) => setNeu({ ...neu, [f]: v })} />)}
        {liste === "Tagesablauf" ? <Ankreuzfeld label="ca." an={ca} onChange={setCa} /> : null}
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={hinzufuegen}>Hinzufügen</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Schließen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
